/**
 * Página de Transferência de Usuários entre Áreas
 * Permite transferir usuários de uma área para outra
 * 
 * @author Equipe GestaoeCRM
 * @date 31/10/2025
 * @version 1.0.0
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  ArrowRightLeft,
  Users,
  Building2,
  Check,
  X,
  AlertCircle,
  Loader2,
  User
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import companyAreaService, { CompanyArea } from '@/services/companyAreaService';
import api from '@/services/api';
import LoadingSpinner from '@/components/LoadingSpinner';

export default function AreaTransferPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const areaIdParam = searchParams.get('areaId');
  
  const [sourceAreaId, setSourceAreaId] = useState<string>(areaIdParam || '');
  const [targetAreaId, setTargetAreaId] = useState<string>('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  // Buscar todas as áreas
  const { data: areasResponse, isLoading: areasLoading } = useQuery({
    queryKey: ['company-areas', 'all'],
    queryFn: () => companyAreaService.getAreas({ page: 1, limit: 1000 }),
  });
  const areas: CompanyArea[] = (areasResponse as any)?.data ?? [];

  // Buscar detalhes da área de origem
  const { data: sourceArea, isLoading: sourceAreaLoading } = useQuery({
    queryKey: ['company-area', sourceAreaId],
    queryFn: () => companyAreaService.getAreaById(sourceAreaId),
    enabled: !!sourceAreaId,
  });

  // Mutação para transferir usuários
  const transferMutation = useMutation({
    mutationFn: async ({ userIds, targetAreaId }: { userIds: string[]; targetAreaId: string }) => {
      // Transferir cada usuário atualizando seu areaId
      // Isso criará automaticamente atividades em ambas as áreas (origem e destino)
      const promises = userIds.map(userId =>
        api.put(`/users/${userId}`, { areaId: targetAreaId })
      );
      await Promise.all(promises);
      return { success: true };
    },
    onSuccess: () => {
      // Invalidar todas as queries relacionadas para forçar atualização
      queryClient.invalidateQueries({ queryKey: ['company-areas'] });
      queryClient.invalidateQueries({ queryKey: ['company-area', sourceAreaId] });
      if (targetAreaId) {
        queryClient.invalidateQueries({ queryKey: ['company-area', targetAreaId] });
      }
      
      // Invalidar e refazer fetch das timelines de ambas as áreas
      queryClient.invalidateQueries({ queryKey: ['area-activities', sourceAreaId] });
      queryClient.invalidateQueries({ queryKey: ['area-activities', targetAreaId] });
      queryClient.refetchQueries({ queryKey: ['area-activities', sourceAreaId] });
      queryClient.refetchQueries({ queryKey: ['area-activities', targetAreaId] });
      
      // Invalidar lista de usuários
      queryClient.invalidateQueries({ queryKey: ['users'] });
      
      toast.success('Usuários transferidos com sucesso!');
      // Resetar seleções
      setSelectedUserIds(new Set());
      setTargetAreaId('');
      // Opcional: voltar para a página de detalhes da área de origem
      if (sourceAreaId) {
        navigate(`/administration/company-areas/${sourceAreaId}`);
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erro ao transferir usuários');
    },
  });

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (!sourceArea?.users) return;
    if (selectedUserIds.size === sourceArea.users.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(sourceArea.users.map(u => u.id)));
    }
  };

  const handleTransfer = () => {
    if (!targetAreaId) {
      toast.error('Selecione a área de destino');
      return;
    }
    if (selectedUserIds.size === 0) {
      toast.error('Selecione pelo menos um usuário');
      return;
    }
    if (sourceAreaId === targetAreaId) {
      toast.error('A área de origem e destino devem ser diferentes');
      return;
    }
    
    transferMutation.mutate({
      userIds: Array.from(selectedUserIds),
      targetAreaId,
    });
  };

  useEffect(() => {
    if (areaIdParam) {
      setSourceAreaId(areaIdParam);
    }
  }, [areaIdParam]);

  if (areasLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-glass-hover">
      {/* Header */}
      <div className="bg-background-glass backdrop-blur-glass shadow-glass border-b border-border-glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between py-4 gap-4">
            <div className="flex-1 min-w-0 w-full lg:w-auto">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-2">
                <button
                  onClick={() => navigate(sourceAreaId ? `/administration/company-areas/${sourceAreaId}` : '/administration/company-areas')}
                  className="flex items-center text-text-muted hover:text-text-secondary transition-all duration-300 w-fit"
                >
                  <ArrowLeft className="h-5 w-5 mr-1 flex-shrink-0" />
                  <span>Voltar</span>
                </button>
              </div>
              
              <h1 className="text-lg sm:text-xl font-semibold text-text-primary break-words">
                Transferir Usuários entre Áreas
              </h1>
              
              <p className="text-sm text-text-muted mt-2">
                Selecione usuários de uma área e transfira para outra área da empresa
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Conteúdo */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Área de Origem */}
          <div className="bg-background-glass backdrop-blur-glass border border-border-glass rounded-glass p-6">
            <h2 className="text-lg font-bold text-text-primary mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-secondary" />
              Área de Origem
            </h2>
            
            <div className="mb-4">
              <label className="block text-sm font-bold text-text-secondary mb-2">
                Selecione a Área
              </label>
              <select
                value={sourceAreaId}
                onChange={(e) => setSourceAreaId(e.target.value)}
                className="input disabled:bg-background-glass-hover disabled:cursor-not-allowed"
                disabled={!!areaIdParam}
              >
                <option value="">Selecione uma área...</option>
                {areas
                  .filter(area => area.active)
                  .map(area => (
                    <option key={area.id} value={area.id}>
                      {area.name}
                    </option>
                  ))}
              </select>
            </div>

            {sourceAreaLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-secondary" />
                <span className="ml-2 text-sm text-text-muted">Carregando usuários...</span>
              </div>
            ) : sourceArea?.users && sourceArea.users.length > 0 ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-bold text-text-secondary">
                    Usuários ({sourceArea.users.length})
                  </label>
                  <button
                    onClick={handleSelectAll}
                    className="text-xs text-secondary hover:text-primary"
                  >
                    {selectedUserIds.size === sourceArea.users.length ? 'Deselecionar Todos' : 'Selecionar Todos'}
                  </button>
                </div>
                
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {sourceArea.users.map((user) => (
                    <label
                      key={user.id}
                      className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedUserIds.has(user.id)
                          ? 'bg-secondary/20 border-secondary'
                          : 'bg-background-glass-hover border-border-glass hover:bg-background-glass'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedUserIds.has(user.id)}
                        onChange={() => handleToggleUser(user.id)}
                        className="mr-3 w-4 h-4 text-secondary focus:ring-secondary rounded"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-text-primary">{user.name}</p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            user.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {user.active ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-text-muted">{user.email}</span>
                          <span className="text-xs text-secondary">{user.role}</span>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ) : sourceAreaId ? (
              <div className="text-center py-8">
                <Users className="mx-auto h-12 w-12 text-text-muted opacity-50" />
                <p className="mt-2 text-sm text-text-muted">
                  Nenhum usuário na área selecionada
                </p>
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="mx-auto h-12 w-12 text-text-muted opacity-50" />
                <p className="mt-2 text-sm text-text-muted">
                  Selecione uma área de origem
                </p>
              </div>
            )}
          </div>

          {/* Área de Destino */}
          <div className="bg-background-glass backdrop-blur-glass border border-border-glass rounded-glass p-6">
            <h2 className="text-lg font-bold text-text-primary mb-4 flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-primary" />
              Área de Destino
            </h2>
            
            <div className="mb-4">
              <label className="block text-sm font-bold text-text-secondary mb-2">
                Selecione a Área de Destino
              </label>
              <select
                value={targetAreaId}
                onChange={(e) => setTargetAreaId(e.target.value)}
                className="input disabled:bg-background-glass-hover disabled:cursor-not-allowed"
                disabled={!sourceAreaId || selectedUserIds.size === 0}
              >
                <option value="">Selecione uma área...</option>
                {areas
                  .filter(area => area.active && area.id !== sourceAreaId)
                  .map(area => (
                    <option key={area.id} value={area.id}>
                      {area.name}
                    </option>
                  ))}
              </select>
            </div>

            {targetAreaId && (
              <div className="mt-4">
                <div className="bg-background-glass-hover rounded-lg p-4 border border-border-glass">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-secondary" />
                    <p className="text-sm font-medium text-text-primary">
                      {areas.find(a => a.id === targetAreaId)?.name}
                    </p>
                  </div>
                  <p className="text-xs text-text-muted">
                    {selectedUserIds.size} usuário(s) será(ão) transferido(s) para esta área
                  </p>
                </div>
              </div>
            )}

            {selectedUserIds.size > 0 && (
              <div className="mt-6">
                <button
                  onClick={handleTransfer}
                  disabled={!targetAreaId || transferMutation.isPending}
                  className="w-full px-4 py-3 border border-transparent rounded-button shadow-glass text-sm font-bold text-white bg-gradient-to-r from-primary to-secondary hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {transferMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Transferindo...
                    </>
                  ) : (
                    <>
                      <ArrowRightLeft className="w-4 h-4" />
                      Transferir {selectedUserIds.size} Usuário(s)
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

